import { MecordColors } from "Constants/Color";
import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { showCustomSwal } from "helpers/swalHelper";
import Swal from "sweetalert2";

export interface ExportOption {
  label: string;
  onExport: () => Promise<{ blob: any; filename: string }>;
}

interface ExportDropdownButtonProps {
  label?: string;
  icon?: string;
  options: ExportOption[];
  className?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const ExportDropdownButton: React.FC<ExportDropdownButtonProps> = ({
  label = "Export",
  icon = "bi bi-box-arrow-up",
  options,
  className = "",
  disabled = false,
  style,
}) => {
  const themeColor = MecordColors?.ThemeBGColor;
  const [isExporting, setIsExporting] = useState(false);

  const baseClasses = `btn custom-export-button ${className}`.trim();
  const iconClasses = icon ? `${icon} align-baseline me-1` : "";

  const buttonStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderColor: themeColor,
    color: themeColor,
    borderRadius: "4px",
    padding: "4px 12px",
    height: "32px",
    minHeight: "32px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: disabled || isExporting ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    opacity: disabled || isExporting ? 0.6 : 1,
    border: `1px solid ${themeColor}`,
    ...style,
  };

  const handleExport = async (option: ExportOption) => {
    if (disabled || isExporting) return;

    setIsExporting(true);

    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await option.onExport();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export data";

      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <style>
        {`
          .custom-export-button {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            border-radius: 4px !important;
            padding: 4px 12px !important;
            height: 32px !important;
            min-height: 32px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            border: 1px solid ${themeColor} !important;
          }
          .custom-export-button:hover:not(:disabled) {
            border-color: ${themeColor} !important;
            opacity: 1 !important;
          }
          .custom-export-button:focus {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
          }
          .custom-export-button:disabled {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }
          .export-dropdown-menu .dropdown-item {
            font-size: 13px;
            padding: 6px 12px;
          }
          .export-dropdown-menu .dropdown-item:hover {
            background-color: ${themeColor}15;
            color: ${themeColor};
          }
        `}
      </style>
      <Dropdown align="end">
        <Dropdown.Toggle
          as="button"
          type="button"
          className={`hstack gap-2 ${baseClasses}`}
          disabled={disabled || isExporting}
          style={buttonStyle}
        >
          {icon && <i className={iconClasses}></i>}
          {label}
        </Dropdown.Toggle>
        <Dropdown.Menu className="export-dropdown-menu">
          {options.map((option) => (
            <Dropdown.Item
              key={option.label}
              onClick={() => handleExport(option)}
              disabled={disabled || isExporting}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default ExportDropdownButton;
